import React, { memo, useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { getVehicleStatusDisplay } from "utils/vehicleStatusDisplay";
import "./InventoryInfoCards.css";

const InventoryInfoCards = memo(({ data, activeColor, getActiveColorStyle, formatUSD, vehicleAge, daysInInventory, onImagesCountClick }) => {
  const { t } = useTranslation();
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [showPricingDetails, setShowPricingDetails] = useState(false);
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [isCompactScreen, setIsCompactScreen] = useState(
    () => window.innerWidth <= 768
  );

  const vehicleDetailsExpanded = isCompactScreen || showVehicleDetails;
  const pricingDetailsExpanded = isCompactScreen || showPricingDetails;
  const locationDetailsExpanded = isCompactScreen || showLocationDetails;

  const [currentTheme, setCurrentTheme] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  const getTextColor = () => {
    return currentTheme ? 'white' : 'black';
  };

  const vehicleStatusDisplay = getVehicleStatusDisplay(data?.inventory_vehicle?.vehicle_status);

  useEffect(() => {
    const handleStorageChange = () => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setCurrentTheme(newTheme);
    };

    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentTheme]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = () => setIsCompactScreen(mql.matches);

    handleChange();
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="d-flex flex-column flex-lg-row" style={{ gap: 12, alignItems: 'stretch' }}>
      {/* 1. Kutu - Vehicle Information */}
      <Card style={{
        width: "100%",
        borderLeft: `8px solid ${vehicleStatusDisplay.color || data?.inventory_vehicle?.vehicle_status?.color}`,
        height: '100%',
        minHeight: 150,
      }}>
        <CardHeader className="pt-1">
          <h4 className="mb-0">{t('inventoryInfoCards.inventoryDetail')}</h4>
        </CardHeader>
        <CardBody style={{ paddingTop: 5, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1 }}>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.status')}:</span>
              <span
                className="iic-value"
                style={{
                  color: vehicleStatusDisplay.color,
                  fontWeight: vehicleStatusDisplay.fontWeight,
                }}
              >
                {vehicleStatusDisplay.label}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.description')}:</span>
              <span className="iic-value" style={{
                color: getTextColor(),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px'
              }}>
                {data?.inventory_vehicle?.year + " "}  {data?.inventory_vehicle?.make + " "} {data?.inventory_vehicle?.model}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.vin')}:</span>
              <span className="iic-value" style={{
                color: getTextColor(),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px'
              }}>{data?.inventory_vehicle?.vin || '-'}</span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.stockNumber')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>{data?.stock_number || '-'}</span>
            </div>
            {vehicleDetailsExpanded && (
              <>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.bodyTrim')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>{data?.inventory_vehicle?.trim || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.bodyStyle')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>{data?.inventory_vehicle?.body_style || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.type')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>{data?.inventory_vehicle?.type?.toLocaleUpperCase() || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.color')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>{data?.inventory_vehicle?.exterior_color || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.imagesAvailable')}:</span>
                  {onImagesCountClick ? (
                    <button
                      type="button"
                      className="iic-value iic-value-link"
                      style={{ color: getActiveColorStyle().borderColor }}
                      onClick={onImagesCountClick}
                    >
                      {data?.images_count || 0}
                    </button>
                  ) : (
                    <span className="iic-value" style={{ color: getTextColor() }}>{data?.images_count || 0}</span>
                  )}
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.actualMileage')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>{data?.inventory_vehicle?.actual_mileage || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.condition')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>{data?.inventory_vehicle?.condition || '-'}</span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.vehicleAge')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>{vehicleAge !== undefined ? `${vehicleAge} ${t('inventoryInfoCards.years')}` : '-'}</span>
                </div>
              </>
            )}
          </div>
          {!isCompactScreen && (
          <div style={{ paddingBottom: 10 }}>
            <a
              href="#vehicle-details"
              className="iic-link"
              style={{ color: getActiveColorStyle().borderColor }}
              onClick={(e) => {
                e.preventDefault();
                setShowVehicleDetails(!showVehicleDetails);
              }}
            >
              {showVehicleDetails ? t('inventoryInfoCards.lessDetails') : t('inventoryInfoCards.moreDetails')}
            </a>
          </div>
          )}
        </CardBody>
      </Card>

      {/* 2. Kutu - Pricing Information */}
      <Card style={{
        width: "100%",
        borderLeft: `8px solid rgb(194, 218, 231)`,
        height: '100%',
        minHeight: 150,
      }}>
        <CardHeader className="pt-1">
          <h4 className="mb-0">{t('inventoryInfoCards.pricingInformation')}</h4>
        </CardHeader>
        <CardBody style={{ paddingTop: 5, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1 }}>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.askingPrice')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {formatUSD(data?.inventory_pricing?.normal_asking_price)}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.askingDown')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {formatUSD(data?.inventory_pricing?.asking_down)}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.cost')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {formatUSD(data?.inventory_pricing?.vehicle_cost)}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.vehicleExpenses')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {formatUSD(data?.vehicle_expenses)}
              </span>
            </div>
            {pricingDetailsExpanded && (
              <>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.floorPlanExpense')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>
                    {formatUSD(data?.floor_plan_expense)}
                  </span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.totalCost')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>
                    {formatUSD(data?.inventory_pricing?.total_cost_in_car)}
                  </span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.buyersGuide')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>
                    {data?.inventory_pricing?.buyers_guide_json?.guide_type || '-'}
                  </span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.maxTerms')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>
                    {data?.max_terms || '-'}
                  </span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.repairOrders')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>
                    {data?.repair_orders_count || '-'}
                  </span>
                </div>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.repairCost')}:</span>
                  <span className="iic-value" style={{ color: getTextColor() }}>
                    {formatUSD(data?.repair_cost)}
                  </span>
                </div>
              </>
            )}
          </div>
          {!isCompactScreen && (
          <div style={{ paddingBottom: 10 }}>
            <a
              href="#pricing-details"
              className="iic-link"
              style={{ color: getActiveColorStyle().borderColor }}
              onClick={(e) => {
                e.preventDefault();
                setShowPricingDetails(!showPricingDetails);
              }}
            >
              {showPricingDetails ? t('inventoryInfoCards.lessDetails') : t('inventoryInfoCards.moreDetails')}
            </a>
          </div>
          )}
        </CardBody>
      </Card>

      {/* 3. Kutu - Location Information */}
      <Card style={{
        width: "100%",
        borderLeft: `8px solid rgb(194, 218, 231)`,
        height: '100%',
        minHeight: 150,
      }}>
        <CardHeader className="pt-1">
          <h4 className="mb-0">{t('inventoryInfoCards.locationInformation')}</h4>
        </CardHeader>
        <CardBody style={{ paddingTop: 5, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1 }}>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.location')}:</span>
              <span className="iic-value" style={{
                color: getTextColor(),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px'
              }}>
                {data?.location?.name || '-'}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.altLocation')}:</span>
              <span className="iic-value" style={{
                color: getTextColor(),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px'
              }}>{data?.inventory_vehicle?.alt_location?.name || '-'}</span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.inInventory')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {data?.days_at_location === null || data?.days_at_location === undefined ? '-' : `${data?.days_at_location} ${t('inventoryInfoCards.days')}`}
              </span>
            </div>
            <div className="iic-row">
              <span className="iic-label">{t('inventoryInfoCards.daysAtLocation')}:</span>
              <span className="iic-value" style={{ color: getTextColor() }}>
                {data?.days_in_inventory === null || data?.days_in_inventory === undefined ? "-" : `${data?.days_in_inventory} ${t('inventoryInfoCards.days')}`}
              </span>
            </div>
            {locationDetailsExpanded && (
              <>
                <div className="iic-row">
                  <span className="iic-label">{t('inventoryInfoCards.bookkeepingCo')}:</span>
                  <span className="iic-value" style={{
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>{data?.location?.sales_location?.bookkeeping_company?.name || '-'}</span>
                </div>
              </>
            )}
          </div>
          {!isCompactScreen && (
          <div style={{ paddingBottom: 10 }}>
            <a
              href="#location-details"
              className="iic-link"
              style={{ color: getActiveColorStyle().borderColor }}
              onClick={(e) => {
                e.preventDefault();
                setShowLocationDetails(!showLocationDetails);
              }}
            >
              {showLocationDetails ? t('inventoryInfoCards.lessDetails') : t('inventoryInfoCards.moreDetails')}
            </a>
          </div>
          )}
        </CardBody>
      </Card>

      {/* 4. Kutu - J.D. Power Information */}
      <Card style={{
        width: "100%",
        borderLeft: `8px solid rgb(194, 218, 231)`,
        height: '100%',
        minHeight: 150,
      }}>
        <CardHeader className="pt-1">
          <h4 className="mb-0">{t('inventoryInfoCards.jdPowerInformation')}</h4>
        </CardHeader>
        <CardBody style={{ paddingTop: 5 }}>
          <div className="d-flex align-items-start">
            <div className="w-100">
              <div className="d-flex flex-column flex-md-row justify-content-between">
                <div className="mb-3 mb-md-0" style={{ minWidth: 0, flexGrow: 1, paddingRight: 10 }}>
                  <div className="iic-label">J.D. Power:<br />{t('inventoryInfoCards.retail')}</div>
                  <div className="iic-value" style={{
                    fontSize: 18,
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px'
                  }}>
                    {formatUSD(data?.inventory_pricing?.jd_power_json?.retail?.adj_total)}
                  </div>
                </div>
                <div style={{ minWidth: 0, flexShrink: 0 }}>
                  <div className='text-muted'>{t('inventoryInfoCards.wholesale')}</div>
                  <div className="iic-value" style={{
                    fontSize: 18,
                    color: getTextColor(),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px'
                  }}>
                    {formatUSD(data?.inventory_pricing?.jd_power_json?.trade_in?.adj_total) || t('inventoryInfoCards.na')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if data actually changes
  return (
    prevProps.data === nextProps.data &&
    prevProps.activeColor === nextProps.activeColor &&
    prevProps.vehicleAge === nextProps.vehicleAge &&
    prevProps.daysInInventory === nextProps.daysInInventory &&
    prevProps.onImagesCountClick === nextProps.onImagesCountClick
  );
});

InventoryInfoCards.displayName = 'InventoryInfoCards';

export default InventoryInfoCards; 